import React from 'react';
import { Smartphone, CreditCard, Landmark, Wallet, Truck, Globe, ShieldCheck } from 'lucide-react';
import OptimizedInput from '../OptimizedInput';

const inputCls = 'w-full border border-ink-faint rounded-sm px-3.5 py-2.5 text-sm bg-surface focus:ring-1 focus:ring-accent focus:border-accent';
const labelCls = 'dpc-label text-ink-faint block mb-1.5';

const METHODS = [
  { id: 'MPESA', icon: Smartphone, label: 'M-Pesa', tag: 'Most popular' },
  { id: 'CREDIT_CARD', icon: CreditCard, label: 'Card', tag: 'Secure' },
  { id: 'BANK_TRANSFER', icon: Landmark, label: 'Bank transfer', tag: 'Traditional' },
  { id: 'PAYPAL', icon: Globe, label: 'PayPal', tag: 'International' },
  { id: 'CASH_ON_DELIVERY', icon: Truck, label: 'Cash on delivery', tag: 'Convenient' },
  { id: 'DIGITAL_WALLET', icon: Wallet, label: 'Digital wallet', tag: 'Modern' },
];

const formatMpesaNumber = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('7')) return `254${digits}`;
  if (digits.startsWith('0')) return `254${digits.slice(1)}`;
  return digits;
};

const PaymentStep = ({
  checkoutFormData,
  inputHandlers,
  formErrors,
  checkoutTotals,
  isSubmitting,
  onBack,
  onSubmit,
}) => {
  const method = checkoutFormData.paymentMethod;

  return (
    <div className="p-6 sm:p-8">
      <h3 className="font-display text-xl text-ink mb-5">Choose how to pay</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {METHODS.map(({ id, icon: Icon, label, tag }) => (
          <button
            key={id}
            type="button"
            onClick={() => inputHandlers.paymentMethod(id)}
            className={`text-left p-4 rounded-md border-2 transition-colors ${
              method === id ? 'border-accent bg-accent-soft' : 'border-line hover:border-ink-faint'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <Icon className="w-5 h-5 text-ink" />
              <span className="text-[10px] dpc-label text-ink-faint">{tag}</span>
            </div>
            <p className="text-sm font-medium text-ink mb-1">{label}</p>
            {method === id && <p className="text-xs text-accent">KSh {checkoutTotals.total.toLocaleString()}</p>}
          </button>
        ))}
      </div>
      {formErrors.paymentMethod && <p className="text-xs text-accent mb-6">{formErrors.paymentMethod}</p>}

      {method === 'CREDIT_CARD' && (
        <div className="mb-8 space-y-4">
          <div className="flex items-center gap-2 text-xs text-ink-soft bg-paper border border-line rounded-md p-3">
            <ShieldCheck className="w-4 h-4 text-accent flex-shrink-0" /> Card details are processed securely by Stripe.
          </div>
          <div>
            <label className={labelCls}>Card number</label>
            <OptimizedInput value={checkoutFormData.cardNumber} onChange={inputHandlers.cardNumber} className={inputCls} placeholder="4242 4242 4242 4242" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className={labelCls}>Name on card</label>
              <OptimizedInput value={checkoutFormData.cardName} onChange={inputHandlers.cardName} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Expiry</label>
              <OptimizedInput value={checkoutFormData.cardExpiry} onChange={inputHandlers.cardExpiry} className={inputCls} placeholder="MM/YY" />
            </div>
            <div>
              <label className={labelCls}>CVV</label>
              <OptimizedInput value={checkoutFormData.cardCvv} onChange={inputHandlers.cardCvv} className={inputCls} placeholder="123" />
            </div>
          </div>
        </div>
      )}

      {method === 'BANK_TRANSFER' && (
        <div className="mb-8 space-y-4">
          <div>
            <label className={labelCls}>Bank name</label>
            <OptimizedInput value={checkoutFormData.bankName} onChange={inputHandlers.bankName} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Account number</label>
            <OptimizedInput value={checkoutFormData.accountNumber} onChange={inputHandlers.accountNumber} className={inputCls} />
          </div>
          <div className="bg-paper border border-line rounded-md p-4 text-sm text-ink-soft">
            <p className="text-ink font-medium mb-1">Transfer to:</p>
            <p>Equity Bank · Account 1234567890 · Nairobi CBD Branch</p>
          </div>
        </div>
      )}

      {method === 'MPESA' && (
        <div className="mb-8 space-y-4">
          <div>
            <label className={labelCls}>M-Pesa phone number</label>
            <OptimizedInput
              value={checkoutFormData.mobileNumber}
              onChange={(value) => inputHandlers.mobileNumber(formatMpesaNumber(value))}
              className={inputCls}
              placeholder="07XX XXX XXX"
            />
            <p className="text-xs text-ink-faint mt-1.5">Registered M-Pesa number, format 07XX or 2547XX.</p>
          </div>
          <div className="bg-accent-soft border border-accent/20 rounded-md p-4 text-sm text-ink">
            You'll receive an STK push for <strong>KSh {checkoutTotals.total.toLocaleString()}</strong> — enter your M-Pesa PIN to confirm.
          </div>
        </div>
      )}

      {method === 'DIGITAL_WALLET' && (
        <div className="mb-8">
          <label className={labelCls}>Wallet provider</label>
          <select
            value={checkoutFormData.walletProvider}
            onChange={(e) => inputHandlers.walletProvider(e.target.value)}
            className={inputCls}
          >
            <option value="">Select a wallet</option>
            <option value="apple_pay">Apple Pay</option>
            <option value="google_pay">Google Pay</option>
            <option value="samsung_pay">Samsung Pay</option>
            <option value="paypal">PayPal</option>
          </select>
        </div>
      )}

      <div className="bg-paper rounded-md p-5 mb-8 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-ink-soft">Subtotal</span><span className="text-ink">KSh {checkoutTotals.subtotal.toLocaleString()}</span></div>
        <div className="flex justify-between"><span className="text-ink-soft">Shipping</span><span className="text-ink">{checkoutTotals.shipping === 0 ? 'Free' : `KSh ${checkoutTotals.shipping.toLocaleString()}`}</span></div>
        {checkoutTotals.totalCosts > 0 && (
          <div className="flex justify-between"><span className="text-ink-soft">Additional costs</span><span className="text-ink">KSh {checkoutTotals.totalCosts.toLocaleString()}</span></div>
        )}
        <div className="flex justify-between pt-2 border-t border-line font-display text-lg text-ink">
          <span>Total</span><span>KSh {checkoutTotals.total.toLocaleString()}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button type="button" onClick={onBack} className="px-6 py-3.5 border border-line rounded-full dpc-label text-ink-soft hover:border-ink transition-colors">
          Back to details
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting || !method}
          className="flex-1 bg-ink text-paper dpc-label py-3.5 rounded-full hover:bg-accent transition-colors disabled:opacity-40"
        >
          {isSubmitting ? 'Processing…' : 'Complete order'}
        </button>
      </div>
    </div>
  );
};

export default PaymentStep;
